import { ClassNameMap } from "@mui/material";
import { TypographyClasses, TypographyProps } from "@mui/material/Typography";
import isEmpty from "lodash/isEmpty";
import React, { HTMLAttributes } from "react";
import { MuiTypography } from "..";
import { EMPTY_VALUE_DASH } from "../../helpers/constants/common";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { isValueEmptyOrDash } from "../../helpers/utils";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";
import { MuiDoubleLabelWrapper } from "./MuiDoubleLabel.style";

export interface MuiDoubleLabelProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: string | React.ReactNode;
  labelTypoVariant?: TypographyProps["variant"];
  valueTypoVariant?: TypographyProps["variant"];
  labelClasses?: Partial<TypographyClasses> & Partial<ClassNameMap<never>>;
  valueClasses?: Partial<TypographyClasses> & Partial<ClassNameMap<never>>;
  isValueWithEllipsis?: boolean;
  replaceEmptyWithDash?: boolean;
  labelIcon?: React.ReactNode;
}

export const MuiDoubleLabel: React.FC<MuiDoubleLabelProps> = ({
  label,
  value,
  labelTypoVariant = "h3",
  valueTypoVariant = "h4Bold",
  labelClasses,
  valueClasses,
  isValueWithEllipsis = false,
  replaceEmptyWithDash = false,
  labelIcon,
  ...rest
}) => {
  const renderStringValue = () => {
    const shouldShowEllipseLabel =
      isValueWithEllipsis && !isValueEmptyOrDash(value as string);

    let displayValue: string | React.ReactNode;
    if (replaceEmptyWithDash) {
      displayValue = isEmpty(value) ? EMPTY_VALUE_DASH : (value as string);
    } else {
      displayValue = value as string;
    }

    return shouldShowEllipseLabel ? (
      <MuiEllipseLabel
        labelTypoVariant={valueTypoVariant}
        text={displayValue}
        labelTypoClassName={`value-style max-width ${valueClasses}`}
      />
    ) : (
      <MuiTypography
        component={"h4"}
        data-testid={COMPONENT_IDS.COMMON_DOUBLE_LABEL_VALUE}
        className="value-style"
        variant={valueTypoVariant}
        classes={valueClasses}
      >
        {displayValue}
      </MuiTypography>
    );
  };

  return (
    <MuiDoubleLabelWrapper {...rest}>
      <MuiTypography
        component={"h3"}
        data-testid={COMPONENT_IDS.COMMON_DOUBLE_LABEL}
        className="label-style"
        variant={labelTypoVariant}
        classes={labelClasses}
      >
        {label} {labelIcon}
      </MuiTypography>

      {renderStringValue()}
    </MuiDoubleLabelWrapper>
  );
};
